import { useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { postRegion } from '../../api/regionAPI';

const AddRegionDialog = ({ open, onClose, regions, onRegionAdded }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentRegion: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const getEligibleParentRegions = () => {
    if (!regions || regions.length === 0) return [];

    const eligibleRegions = regions.filter(region => {
      const level = getRegionLevel(region, regions);
      return level === 1 || level === 2;
    });

    return eligibleRegions;
  };

  const getRegionLevel = (region, allRegions) => {
    if (!region) return 0;
    if (region.parentRegion === null) return 1;

    const parentRegion = allRegions.find(r => r.idRegion === region.parentRegion);
    if (!parentRegion) return 1;

    return getRegionLevel(parentRegion, allRegions) + 1;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.name) {
        setError(t('regions.dialogs.addRegion.nameRequired', 'Name alanı zorunludur'));
        return;
      }

      const adminId = localStorage.getItem('adminId');
      if (!adminId) {
        setError(t('regions.dialogs.addRegion.adminNotFound', 'Admin ID bulunamadı. Lütfen tekrar giriş yapın.'));
        return;
      }

      const requestData = {
        code: formData.name,
        name: formData.name,
        description: formData.description,
        admin_idAdmin: parseInt(adminId),
        parentRegion: formData.parentRegion || null
      };

      await postRegion(requestData);

      setSnackbar({
        open: true,
        message: t('regions.messages.regionCreated'),
        severity: 'success'
      });

      if (onRegionAdded) {
        onRegionAdded();
      }

      setFormData({
        name: '',
        description: '',
        parentRegion: ''
      });
      onClose();
    } catch (err) {
      setError(err.message || t('regions.messages.errorCreatingRegion', 'Region eklenirken bir hata oluştu'));
      console.error('Error adding region:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        parentRegion: ''
      });
      setError(null);
      onClose();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const eligibleParentRegions = getEligibleParentRegions();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('regions.dialogs.addRegion.title')}</DialogTitle>
      <DialogContent dividers sx={{ pb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="dense"
          name="name"
          label={t('regions.dialogs.addRegion.name')}
          type="text"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={handleInputChange}
          disabled={loading}
          required
          sx={{ mb: 2, mt: 1 }}
        />

        <TextField
          margin="dense"
          name="description"
          label={t('regions.dialogs.addRegion.descriptionField')}
          type="text"
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          value={formData.description}
          onChange={handleInputChange}
          disabled={loading}
          sx={{ mb: 2 }}
          InputProps={{
            sx: { alignItems: 'flex-start' }
          }}
        />

        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>{t('regions.dialogs.addRegion.parentRegion')}</InputLabel>
          <Select
            name="parentRegion"
            value={formData.parentRegion}
            onChange={handleInputChange}
            label={t('regions.dialogs.addRegion.parentRegion')}
            disabled={loading}
          >
            <MenuItem value="">
              <em>{t('regions.dialogs.addRegion.none')}</em>
            </MenuItem>
            {eligibleParentRegions.map((region) => {
              const level = getRegionLevel(region, regions);
              const levelLabel = level === 1 ? 'Root' : level === 2 ? 'Region' : `Level ${level}`;
              return (
                <MenuItem key={region.idRegion} value={region.idRegion}>
                  {region.name} ({region.code}) - {levelLabel}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t('common.saving') : t('common.add')}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddRegionDialog;
